import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select, Store } from '@ngxs/store';
import {
  PersonalDetails,
  PersonalizationPerson,
  ProductState,
  SetFirstPerson,
  ShoppingCartBillingState,
  ShoppingCartPersonalizationState
} from '@platform-frontend/checkout';
import { ProductPersonalizationAdditionAddressRequirements } from '@platform-frontend/marketplace';
import { CustomValidators, DateInputComponent, DateSelectionValue, InclusiveActionFilter, ToggleState } from '@platform-frontend/ui-tools';
import { Navigate } from '@platform-frontend/util';
import { map, Observable, switchMap, tap } from 'rxjs';

interface CreatePersonForm {
  isUser: FormControl<boolean>;
  firstName: FormControl<string | null>;
  lastName: FormControl<string | null>;
  birthDate: FormControl<DateSelectionValue>;
  country: FormControl<string>;
  zipCode: FormControl<string>;
}

@UntilDestroy()
@Component({
  selector: 'yat-create-person',
  templateUrl: './create-person.component.html',
  styleUrls: ['./create-person.component.scss']
})
export class CreatePersonComponent implements OnInit {
  constructor(private activatedRoute: ActivatedRoute, private store: Store) {}

  @ViewChild('firstNameInput') firstNameInput: ElementRef;
  @ViewChild('lastNameInput') lastNameInput: ElementRef;
  @ViewChild(DateInputComponent) child: DateInputComponent;

  @Select(ShoppingCartPersonalizationState.userPersonalDetails)
  userPersonalDetails$: Observable<PersonalDetails<string | null> | null>;

  @Select(ProductState.mustIncludeUser)
  mustIncludeUser$: Observable<boolean>;

  @Select(ProductState.maxNumberOfPersons)
  maxNumberOfPersons$: Observable<number>;

  @Select(ShoppingCartBillingState.selectedCountry)
  selectedCountry$: Observable<string>;

  createPersonForm = new FormGroup<CreatePersonForm>({
    isUser: new FormControl<boolean>(false, { nonNullable: true }),
    firstName: new FormControl<string | null>(null, { validators: Validators.required }),
    lastName: new FormControl<string | null>(null, { validators: Validators.required }),
    birthDate: new FormControl<DateSelectionValue>(null, {
      validators: [Validators.required, CustomValidators.invalidDateValidator(), CustomValidators.oldHumanValidator(), CustomValidators.futureDateValidator()]
    }),
    country: new FormControl('', { nonNullable: true, validators: Validators.required }),
    zipCode: new FormControl('', { nonNullable: true, validators: [Validators.required, CustomValidators.invalidZipCodeValidator('DE')] })
  });

  birthDateRequired: boolean = false;
  addressRequirements: ProductPersonalizationAdditionAddressRequirements | null = null;
  showMask: boolean = false;
  setFirstPersonActionFilter = new InclusiveActionFilter(SetFirstPerson);

  ngOnInit(): void {
    this.birthDateRequired = this.store.selectSnapshot(ProductState.birthDateRequirement) !== 'NONE';
    if (!this.birthDateRequired) {
      const birthDateControl = this.createPersonForm.controls.birthDate;
      birthDateControl.clearValidators();
      birthDateControl.updateValueAndValidity();
    }

    this.addressRequirements = this.store.selectSnapshot(ProductState.addressRequirements);
    const zipCodeControl = this.createPersonForm.controls.zipCode;
    const countryControl = this.createPersonForm.controls.country;
    if (!this.addressRequirements?.countryRequired) {
      countryControl.clearValidators();
      countryControl.updateValueAndValidity();
      zipCodeControl.clearValidators();
      zipCodeControl.updateValueAndValidity();
    } else if (this.addressRequirements) {
      this.createPersonForm.controls.country.valueChanges
        .pipe(
          tap(country =>
            this.isZipCodeRequiredForCountry(country)
              ? zipCodeControl.setValidators([Validators.required, CustomValidators.invalidZipCodeValidator(country)])
              : zipCodeControl.clearValidators()
          ),
          tap(() => zipCodeControl.updateValueAndValidity()),
          untilDestroyed(this)
        )
        .subscribe();
    }

    // TODO: MP-8794 Create a new ticket to get the user's personal country and ZIP code from the BE
    const firstPerson: PersonalizationPerson<string | null> | null = this.store.selectSnapshot(ShoppingCartPersonalizationState.firstPerson);
    const selectedCountry: string = this.store.selectSnapshot(ShoppingCartBillingState.selectedCountry) ?? '';
    this.createPersonForm.patchValue({
      isUser: firstPerson?.isUser ?? true,
      country: selectedCountry
    });
    this.patchPersonalDetails(firstPerson);
  }

  cancel(): void {
    this.store.dispatch(new Navigate([this.getNavigationRoute()], { relativeTo: this.activatedRoute }));
  }

  toggleChanged(state: ToggleState): void {
    this.createPersonForm.patchValue({
      isUser: state === 'left'
    });

    const userPersonalDetails: PersonalDetails<string | null> | null = this.store.selectSnapshot(ShoppingCartPersonalizationState.userPersonalDetails);
    if (userPersonalDetails) {
      const firstPerson: PersonalizationPerson<string | null> | null = this.store.selectSnapshot(ShoppingCartPersonalizationState.firstPerson);
      if (state === 'left') this.patchPersonalDetails(userPersonalDetails);
      else if (firstPerson?.isUser === false) this.patchPersonalDetails(firstPerson);
      else {
        this.createPersonForm.reset();
        const selectedCountry: string = this.store.selectSnapshot(ShoppingCartBillingState.selectedCountry) ?? '';
        this.createPersonForm.patchValue({
          country: selectedCountry
        });
        const event = new Event('blur');
        this.firstNameInput.nativeElement.dispatchEvent(event);
        this.lastNameInput.nativeElement.dispatchEvent(event);
        this.child.handleBlur('');
      }
    }
  }

  isZipCodeRequiredForCountry(country: string): boolean {
    return (
      this.addressRequirements !== null &&
      this.addressRequirements.zipCodeRequiredForCountries !== null &&
      this.addressRequirements.zipCodeRequiredForCountries.includes(country)
    );
  }

  private patchPersonalDetails(person: PersonalDetails<string | null> | null): void {
    if (this.birthDateRequired) {
      this.createPersonForm.patchValue({ birthDate: person?.birthDate ?? null });
    }

    this.createPersonForm.patchValue({
      firstName: person?.firstName ?? '',
      lastName: person?.lastName ?? ''
    });
  }

  submitForm(firstPerson: {
    firstName: string | null;
    lastName: string | null;
    isUser: boolean;
    birthDate: DateSelectionValue;
    zipCode: string;
    country: string;
  }): void {
    const firstPersonPersonalDetails: PersonalDetails<string | null> = {
      firstName: firstPerson.firstName?.trim().length === 0 ? null : firstPerson.firstName,
      lastName: firstPerson.lastName?.trim().length === 0 ? null : firstPerson.lastName,
      birthDate: this.birthDateRequired ? (firstPerson.birthDate === 'INVALID' ? null : firstPerson.birthDate) : null,
      address:
        this.addressRequirements && this.addressRequirements.countryRequired
          ? {
              zipCode: this.isZipCodeRequiredForCountry(firstPerson.country) ? firstPerson.zipCode : null,
              country: firstPerson.country
            }
          : null
    };

    const fullName: string | null =
      firstPersonPersonalDetails.firstName || firstPersonPersonalDetails.lastName
        ? (firstPerson.firstName ?? '') + (firstPerson.firstName && firstPerson.lastName ? ' ' : '') + (firstPerson.lastName ?? '')
        : null;

    this.store
      .dispatch(new SetFirstPerson({ ...firstPersonPersonalDetails, isUser: firstPerson.isUser, fullName }))
      .pipe(
        map(() => this.getNavigationRoute()),
        switchMap(route => this.store.dispatch(new Navigate([route], { relativeTo: this.activatedRoute }))),
        untilDestroyed(this)
      )
      .subscribe();
  }

  private getNavigationRoute(): string {
    const maxNumberOfPersons: number = this.store.selectSnapshot(ProductState.maxNumberOfPersons);
    const personalizationDataSet: boolean = this.store.selectSnapshot(ShoppingCartPersonalizationState.arePersonsSet);
    return maxNumberOfPersons > 1 && personalizationDataSet ? '../multiple-person' : '../overview';
  }
}
